// Central colour palette for the Tree Management System — single source of
// truth so every page (Stock, Field, Loads, Yard, TV Dashboard...) renders
// the same swatch for a given colour code instead of each page guessing.
export const COLOR_HEX = {
  black: '#1A1A1A',
  blue: '#2563EB',
  green: '#16A34A',
  grey: '#6B7280',
  silver: '#6B7280',
  orange: '#F97316',
  pink: '#EC4899',
  red: '#DC2626',
  'red stripe': '#DC2626',
  turquoise: '#14B8A6',
  white: '#E5E7EB',
  yellow: '#EAB308',
  gold: '#CA8A04',
};

// Swatches offered when adding/editing a colour code on the Stock page —
// matches the production Wilson Contractors tree colour range, plus extras.
export const COLOR_PALETTE = [
  { color: 'black', label: 'Black', hex: '#1A1A1A' },
  { color: 'blue', label: 'Blue', hex: '#2563EB' },
  { color: 'green', label: 'Green', hex: '#16A34A' },
  { color: 'grey', label: 'Grey', hex: '#6B7280' },
  { color: 'orange', label: 'Orange', hex: '#F97316' },
  { color: 'pink', label: 'Pink', hex: '#EC4899' },
  { color: 'red stripe', label: 'Red Stripe', hex: '#DC2626', striped: true },
  { color: 'red', label: 'Red', hex: '#DC2626' },
  { color: 'turquoise', label: 'Turquoise', hex: '#14B8A6' },
  { color: 'white', label: 'White', hex: '#F8FAFC', outline: true },
  { color: 'yellow', label: 'Yellow', hex: '#EAB308' },
  { color: 'gold', label: 'Gold', hex: '#CA8A04' },
];

const isRedStripe = (k) => k === 'red stripe' || k === 'red_stripe';
const isHex = (v) => typeof v === 'string' && v.startsWith('#');
const STRIPE_BG = 'repeating-linear-gradient(45deg, #DC2626, #DC2626 4px, #FFFFFF 4px, #FFFFFF 8px)';

// Any colour can have its own custom swatch (set via the Stock page "Edit
// Colour" dialog) stored on the tree_colours document, independent of its
// `color` key/name. The stored value is either a palette KEY (e.g. "red
// stripe", "white" — so its stripe/outline treatment is preserved) or a raw
// "#hex" for a genuinely custom shade. Pages call registerColours(colours)
// right after fetching colours so every lookup below picks up overrides
// without each page having to thread the list around.
let swatchOverrides = {};
export const registerColours = (colours) => {
  (colours || []).forEach(c => {
    if (c?.color) swatchOverrides[c.color.toLowerCase().trim()] = c.swatch || null;
  });
};

// Resolves an override to either a literal hex ({hex}) or another palette
// key whose pattern rules should be applied ({effectiveKey}).
const resolve = (key) => {
  const k = (key || '').toLowerCase().trim();
  const override = swatchOverrides[k];
  if (isHex(override)) return { hex: override };
  return { effectiveKey: override || k };
};

export const colourHex = (key) => {
  const r = resolve(key);
  if (r.hex) return r.hex;
  return COLOR_HEX[r.effectiveKey] || '#1A4331';
};

// Swatch background style — handles the "red stripe" diagonal pattern and a
// visible outline for "white" so both are distinguishable on a white card.
export const swatchStyle = (key) => {
  const r = resolve(key);
  if (r.hex) return { backgroundColor: r.hex };
  if (isRedStripe(r.effectiveKey)) return { backgroundImage: STRIPE_BG };
  if (r.effectiveKey === 'white') return { backgroundColor: colourHex(r.effectiveKey), border: '1px solid #CBD5E1' };
  return { backgroundColor: colourHex(r.effectiveKey) };
};

// Pill/badge style used for small colour indicators on dark backgrounds
// (e.g. TV dashboard) — solid swatch colour with a soft glow, with the same
// red/white diagonal stripe pattern as swatchStyle() for "red stripe" so it
// doesn't read as plain Red on the TV screen.
export const pillStyle = (key) => {
  const r = resolve(key);
  if (r.hex) return { backgroundColor: r.hex, border: '1px solid rgba(255,255,255,0.3)' };
  if (isRedStripe(r.effectiveKey)) return { backgroundImage: STRIPE_BG, border: '1px solid rgba(255,255,255,0.5)' };
  return { backgroundColor: colourHex(r.effectiveKey), border: '1px solid rgba(255,255,255,0.3)' };
};

export const pillGlow = (key) => {
  const r = resolve(key);
  if (r.hex) return { backgroundColor: r.hex, boxShadow: `0 0 8px ${r.hex}` };
  if (isRedStripe(r.effectiveKey)) return { backgroundImage: STRIPE_BG, boxShadow: '0 0 8px #DC2626' };
  return { backgroundColor: colourHex(r.effectiveKey), boxShadow: `0 0 8px ${colourHex(r.effectiveKey)}` };
};
